import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { 
  FaCogs, 
  FaPuzzlePiece, 
  FaUsers, 
  FaHeadset, 
  FaShoppingCart, 
  FaGraduationCap, 
  FaServer 
} from 'react-icons/fa';
import './OurServices.css';
import { card1, card2, card3, card4 } from '../assets';

const services = [
  {
    title: 'NetSuite Implementation',
    desc: 'A successful NetSuite ERP implementation requires expertise and precision.',
    icon: <FaCogs className="service-icon" />,
    color: '#7303c0',
    image: card1,
    path: '/netsuite/services/implementation'
  },
  {
    title: 'NetSuite Staffing',
    desc: 'Find expert NetSuite professionals for implementation, customization, and support to ensure seamless operations.',
    icon: <FaUsers className="service-icon" />,
    color: '#23a6d5',
    image: card3,
    path: '/netsuite/services/staffing'
  },
  {
    title: 'NetSuite Support',
    desc: 'Our team provides ongoing support, debugging, and optimization to ensure your NetSuite system runs efficiently without disruptions.',
    icon: <FaHeadset className="service-icon" />,
    color: '#fcb045',
    image: card4,
    path: '/netsuite/services/support'
  },
  {
    title: 'NetSuite ERP',
    desc: 'Comprehensive ERP solutions to streamline your business processes and improve operational efficiency.',
    icon: <FaServer className="service-icon" />,
    color: '#28a745',
    image: card2,
    path: '/netsuite/services/erp'
  },
  {
    title: 'NetSuite Financial  ',
    desc: 'Expert training and advisory services to maximize your NetSuite investment and user adoption.',
    icon: <FaGraduationCap className="service-icon" />,
    color: '#17a2b8',
    image: card1,
    path: '/netsuite/services/financial'
  },
  {
    title: 'NetSuite CRM',
    desc: 'Advanced CRM solutions to manage customer relationships and drive sales growth effectively.',
    icon: <FaUsers className="service-icon" />,
    color: '#6f42c1',
    image: card3,
    path: '/netsuite/services/crm'
  },
  {
    title: 'NetSuite E-commerce',
    desc: 'Integrated e-commerce solutions to power your online business and enhance customer experience.',
    icon: <FaShoppingCart className="service-icon" />,
    color: '#fd7e14',
    image: card4,
    path: '/netsuite/services/ecommerce'
  },
  {
    title: 'NetSuite Customization',
    desc: 'We help businesses optimize NetSuite by customizing workflows, reports, and automation to fit your needs.',
    icon: <FaPuzzlePiece className="service-icon" />,
    color: '#ec38bc',
    image: card2,
    path: '/services'
  }
];

const ServiceCard = ({ service, index }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  React.useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        delay: index * 0.15,
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <Link to={service.path} className="text-decoration-none">
      <motion.div
        ref={ref}
        initial="hidden"
        animate={controls}
        variants={cardVariants}
        className="service-card h-100 rounded-4 shadow-sm"
      >
        {/* <div className="card-image-container">
          <img 
            src={service.image} 
            alt={service.title}
            className="card-top-image"
          />
          <div className="image-overlay" style={{ background: `linear-gradient(135deg, ${service.color}80, transparent)` }}></div>
        </div> */}
        <div className="card-content p-4">
          <div className="icon-wrapper mb-3" style={{ color: service.color }}>
            {service.icon}
          </div>
          <h5 className="fw-bold mb-3 " style={{ color: service.color }}>{service.title}</h5>
          <p className="mb-3 text-black">{service.desc}</p>
          <div className="card-footer mt-auto pt-3 d-flex justify-content-between align-items-center">
            <span className="learn-more-text" style={{ color: service.color }}>Learn more</span>
            <div className="circle-arrow" style={{ backgroundColor: service.color }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-arrow-right" viewBox="0 0 16 16">
                <path fillRule="evenodd" d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8z"/>
              </svg>
            </div>
          </div>
        </div>
        <div className="card-border" style={{ background: `linear-gradient(135deg, ${service.color}, ${service.color}80)` }}></div>
      </motion.div>
    </Link>
  );
};

const OurServices = () => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  React.useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  const textVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section className="services-section py-5 position-relative overflow-hidden ">
      <div className="bg-pattern"></div>
      <Container className='mb-5'>
        <motion.div 
          className="text-center mb-5"
          initial="hidden"
          animate={controls}
          variants={textVariants}
          ref={ref}
        >
          <h6 className="text-gradient fw-bold mb-2">OUR SERVICES</h6>
          <h2 className="fw-bold display-5 mb-3">NetSuite Solutions</h2>
          
          <p className="lead mx-auto" style={{ maxWidth: '700px' }}>
            Our NetSuite solutions streamline operations, enhance efficiency, and drive growth by automating processes,
            improving data accuracy, and providing <strong>real-time business insights.</strong>
          </p>
        </motion.div>

        <Row className="g-4">
          {services.map((service, index) => (
            <Col xl={3} lg={4} md={6} key={index}>
              <ServiceCard service={service} index={index} />
            </Col>
          ))}
        </Row>

        {/* <motion.div 
          className="text-center mt-5 pt-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.5 }}
        >
          <a href="#contact" className="btn btn-gradient btn-lg px-4 py-2">
            Explore Our NetSuite Services
          </a>
        </motion.div> */}
      </Container>
    </section>
  );
};

export default OurServices;